<template>
  <div>
    <InfoMobileMenu
      v-if="isMobile"
      :title="currentTitle"
      :current-path="currentPath"
      :routes="currentRoutes"
    />
    <div
      v-else
      class="mb-6 text-h5 text--text"
    >
      <slot name="title" />
    </div>
    <div
      class="info-content"
      :class="{ 'd-flex ga-8': !isMobile }"
    >
      <div
        v-if="!isMobile"
        class="flex-shrink-0 position-sticky align-self-start sticky-sidebar"
      >
        <InfoSidebar
          :path="currentPath"
          :routes="currentRoutes"
        />
      </div>
      <div :class="{ 'flex-grow-1 info-content': !isMobile }">
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onServerPrefetch } from 'vue'
import InfoSidebar from 'src/components/common/InfoSidebar/InfoSidebar.vue'
import InfoMobileMenu from 'src/layouts/info-pages/InfoMobileMenu.vue'
import { SidebarRoute } from 'src/types/info'
import { useURLStore } from 'src/store'
import useContextStore from 'src/store/useContextStore'

const props = defineProps<{
  currentPath: string
  isPolicy: boolean
}>()

const {
  reverseFullL10N,
  urls,
  patch,
} = useURLStore()

const { context, urlLang } = useContextStore()
const isMobile = context.flavour === 'mobile'
const siteYdoc = 'https://medrocket.me/'

onServerPrefetch(() => {
  patch({
    legal_case: urls.legal_case,
    pressa: urls.pressa,
    info_conferences: urls.info_conferences,
    rate_fraud_free: urls.rate_fraud_free,
    ethical_commission: urls.ethical_commission,
    socials: urls.socials,
    contacts: urls.contacts,
    about_us: urls.about_us,
    document: urls.document,
  })
})

const isRussia = context.request.country.is_russia

function getInfoRoute(name: string) {
  return reverseFullL10N({
    name,
    args: { language: urlLang },
  })
}

const routes: SidebarRoute[] = [
  {
    path: siteYdoc,
    label: 'Компания МедРокет',
    icon: 'ui-icon-home-ProDoctorov',
  },
  {
    path: getInfoRoute('about_us'),
    label: `${isRussia ? 'О ПроДокторов' : 'О YDoc'}`,
    icon: 'ui-icon-rocket',
  },
  {
    path: getInfoRoute('document'),
    label: isRussia ? 'Редакция и юрлицо' : 'Реквизиты компании',
    icon: 'ui-icon-layers',
  },
]

if (isRussia) {
  routes.push(
    {
      path: getInfoRoute('pressa'),
      label: 'О нас говорят',
      icon: 'ui-icon-megaphone',
    },
    {
      path: getInfoRoute('info_conferences'),
      label: 'Выступления',
      icon: 'ui-icon-education',
    },
    {
      path: getInfoRoute('legal_case'),
      label: 'Битва за отзывы',
      icon: 'ui-icon-shield',
    },
    {
      path: getInfoRoute('rate_fraud_free'),
      label: 'Отзывы без обмана',
      icon: 'ui-icon-proverka',
    },
    {
      path: getInfoRoute('ethical_commission'),
      label: 'Этический комитет',
      icon: 'ui-icon-scales',
    },
    {
      path: getInfoRoute('socials'),
      label: 'Соцсети',
      icon: 'ui-icon-message-circle',
    },
  )
}

routes.push({
  path: getInfoRoute('contacts'),
  label: 'Контакты',
  icon: 'ui-icon-phone-call',
})

const countryIso = context.request.country.iso
const cis = ['BY', 'KZ', 'KG', 'TJ', 'UZ', 'AM', 'GE', 'MD', 'AZ']

const policyRoutes: SidebarRoute[] = [
  {
    path: '/info/terms-of-use/',
    label: 'Пользовательское соглашение',
  },
  {
    path: '/info/privacy-policy/',
    label: 'Обработка персональных данных',
    items: [...(isRussia || cis.includes(countryIso))
      ? [
        {
          href: '/info/privacy-policy/#personal',
          label: 'Политика обработки персональных данных',
        },
        {
          href: '/info/privacy-policy/#public',
          label: 'Политика обработки общедоступных персональных данных',
        },
      ]
      : [],
    ...(isRussia
      ? [{
        href: '/info/privacy-policy/#confidential',
        label: 'Политика в области конфиденциальной информации',
      }]
      : [])],
  },
]

if (isRussia) {
  policyRoutes.push(
    {
      path: '/info/publishing-policy/',
      label: 'Публикация материалов',
      items: [
        {
          href: '/info/publishing-policy/#copyright',
          label: 'Политика в области авторских и смежных прав',
        },
        {
          href: '/info/publishing-policy/#publication',
          label: 'Политика публикации материалов',
        },
      ],
    },
    {
      path: '/info/authors-protection/',
      label: 'Условия программы защиты авторов отзывов',
    },
    {
      path: '/info/rate-fraud-free-rules/',
      label: 'Условия программы "Отзывы без обмана"',
    },
  )
} else if (countryIso === 'BY') {
  policyRoutes.push({
    path: '/info/publishing-policy/',
    label: 'Публикация материалов',
    items: [
      {
        href: '/info/publishing-policy/#copyright',
        label: 'Политика в области авторских и смежных прав',
      },
    ],
  })
}

const currentRoutes = computed(() => (
  props.isPolicy ? policyRoutes : routes
))

const currentTitle = computed(() => (
  currentRoutes.value.find(route => route.path === props.currentPath)?.label || ''
))
</script>

<style scoped>
.info-content {
  min-width: 0;
}

.sticky-sidebar {
  top: 20px;
}
</style>
